import { createSafeContext } from "@/helpers/create-safe-context"
import { Dispatch, SetStateAction } from "react"

import type { ActiveNarrationsMap } from "@/components/CopilotAnalyze/types"

export interface CopilotContext {
    /** Whether the Copilot UI is open */
    isCopilotOpen: boolean
    /** Set whether the Copilot UI is open */
    setIsCopilotOpen: Dispatch<SetStateAction<boolean>>

    /** Whether the Copilot UI is in fullscreen mode */
    isFullscreenOpen: boolean
    /** Set whether the Copilot UI is in fullscreen mode */
    setIsFullscreenOpen: Dispatch<SetStateAction<boolean>>

    /** Whether the Sessions List is open */
    isSessionsListOpen: boolean
    /** Set whether the Sessions List is open */
    setIsSessionsListOpen: Dispatch<SetStateAction<boolean>>

    personalizationId?: string
    /** Set the personalization ID */
    setPersonalizationId: Dispatch<SetStateAction<string>>

    /** The active Session ID */
    activeSessionId?: string
    /** Set the active Session ID */
    setActiveSessionId: Dispatch<SetStateAction<string>>

    /** Target for rendering the Session Delete Confirmation Modal */
    containerRef: React.RefObject<HTMLDivElement>

    /** Settings for the Copilot UI - Supports arbitrary keys, but they won't be typed! */
    settings: Record<string, boolean>
    /** Update the settings for the Copilot UI */
    updateSettings: Dispatch<SetStateAction<Record<string, boolean>>>

    /** Tracks which Sessions have unread messages @deprecated */
    unreadSessions: string[]
    /** Set which Sessions have unread messages @deprecated */
    setUnreadSessions: Dispatch<SetStateAction<string[]>>
    /** Mark a Session as read @deprecated */
    markSessionAsRead: (sessionId: string) => void
    /** Add a Session to the unread list @deprecated */
    addUnreadSession: (sessionId: string) => void

    /** Show a loading UI after sending message and before first AI response token */
    isLoading: boolean
    /** Set whether the loading UI is shown */
    setIsLoading: Dispatch<SetStateAction<boolean>>

    /** Reset everything to the initial state to recover from errors */
    resetState: () => void

    /** Callback function called when a new profile is created */
    onProfileCreated?: (profileId: string) => void
    /** Set the callback function for profile creation */
    setOnProfileCreated: (callback?: (profileId: string) => void) => void

    /** Map of message_id to array of thought events for active narration */
    activeNarrations: ActiveNarrationsMap
    /** Set the active narrations */
    setActiveNarrations: Dispatch<SetStateAction<ActiveNarrationsMap>>
}

export const [CopilotProvider, useCopilotContext] = createSafeContext<CopilotContext>(
    "CopilotProvider component was not found in tree",
)
